import { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Button, Spin, message, Modal, Input, Space, Steps, Descriptions,
} from 'antd';
import { workOrders } from '../../../services/api';
import { isViewer } from '../../../utils/auth';

const STATUS_COLORS = {
  '待派发': 'default', '处理中': 'processing', '已完成': 'success', '已关闭': 'default',
};
const PRIORITY_COLORS = { '低': 'blue', '中': 'orange', '高': 'red', '紧急': 'purple' };

function formatTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('zh-CN', { hour12: false });
}

function statusStep(status) {
  const map = { '待派发': 0, '处理中': 1, '已完成': 2, '已关闭': 3 };
  return map[status] ?? 0;
}

export default function WorkOrdersPage() {
  // viewer 只读：隐藏派发/完成/关闭等写操作按钮，仅保留详情查看（后端 403 兜底）
  const readOnly = isViewer();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [list, setList] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [resultNote, setResultNote] = useState('');
  const [actionId, setActionId] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [ovRes, listRes] = await Promise.all([
        workOrders.overview(),
        workOrders.list(),
      ]);
      setOverview(ovRes.data);
      setList(listRes.data || []);
    } catch (err) {
      message.error(err.message || '加载工单数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openDetail = async (id) => {
    try {
      const res = await workOrders.detail(id);
      setSelected(res.data);
      setDetailOpen(true);
    } catch (err) {
      message.error(err.message || '加载详情失败');
    }
  };

  const handleAssign = async (id) => {
    try {
      await workOrders.assign(id, { assignee: '运维值班员' });
      message.success('工单已派发');
      loadData();
    } catch (err) {
      message.error(err.message || '派发失败');
    }
  };

  const handleComplete = async () => {
    try {
      await workOrders.complete(actionId, { result_note: resultNote });
      message.success('工单已完成，关联告警已更新');
      setCompleteOpen(false);
      setResultNote('');
      loadData();
      if (detailOpen && selected?.id === actionId) {
        const res = await workOrders.detail(actionId);
        setSelected(res.data);
      }
    } catch (err) {
      message.error(err.message || '操作失败');
    }
  };

  const handleClose = async (id) => {
    try {
      await workOrders.close(id);
      message.success('工单已关闭');
      loadData();
    } catch (err) {
      message.error(err.message || '关闭失败');
    }
  };

  const columns = [
    { title: '工单编号', dataIndex: 'order_no', width: 160 },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    { title: '优先级', dataIndex: 'priority', render: (p) => <Tag color={PRIORITY_COLORS[p]}>{p}</Tag> },
    { title: '状态', dataIndex: 'status', render: (s) => <Tag color={STATUS_COLORS[s]}>{s}</Tag> },
    { title: '来源', dataIndex: 'source' },
    { title: '处理人', dataIndex: 'assignee', render: (v) => v || '-' },
    { title: '创建时间', dataIndex: 'created_at', render: formatTime },
    {
      title: '操作',
      width: 220,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => openDetail(record.id)}>详情</Button>
          {!readOnly && record.status === '待派发' && (
            <Button type="link" size="small" onClick={() => handleAssign(record.id)}>派发</Button>
          )}
          {!readOnly && record.status === '处理中' && (
            <Button type="link" size="small" onClick={() => { setActionId(record.id); setCompleteOpen(true); }}>完成</Button>
          )}
          {!readOnly && (record.status === '已完成' || record.status === '处理中') && (
            <Button type="link" size="small" onClick={() => handleClose(record.id)}>关闭</Button>
          )}
        </Space>
      ),
    },
  ];

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      {overview && (
        <Row gutter={[16, 16]}>
          <Col span={4}><Card size="small"><Statistic title="工单总数" value={overview.total} suffix="个" /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="今日新增" value={overview.today} suffix="个" valueStyle={{ color: '#1890ff' }} /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="待派发" value={overview.pending} suffix="个" /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="处理中" value={overview.in_progress} suffix="个" valueStyle={{ color: '#faad14' }} /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="已完成" value={overview.completed} suffix="个" valueStyle={{ color: '#52c41a' }} /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="来自告警" value={overview.from_alarm} suffix="个" /></Card></Col>
        </Row>
      )}

      <Card title="运维工单列表" style={{ marginTop: 16 }} size="small">
        <div style={{ marginBottom: 12, fontSize: 12, color: '#888' }}>
          闭环流程：告警确认 → 自动生成工单 → 派发 → 处理 → 完成 → 归档关闭
        </div>
        <Table
          columns={columns}
          dataSource={list.map((o) => ({ ...o, key: o.id }))}
          pagination={{ pageSize: 10, size: 'small' }}
          size="small"
          locale={{ emptyText: '暂无工单，可在告警管理中「确认并生成工单」' }}
        />
      </Card>

      <Modal
        title={selected ? `工单 ${selected.order_no}` : '工单详情'}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={640}
      >
        {selected && (
          <>
            <Steps
              size="small"
              current={statusStep(selected.status)}
              style={{ marginBottom: 20 }}
              items={[
                { title: '待派发' },
                { title: '处理中' },
                { title: '已完成' },
                { title: '已关闭' },
              ]}
            />
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="标题" span={2}>{selected.title}</Descriptions.Item>
              <Descriptions.Item label="优先级"><Tag color={PRIORITY_COLORS[selected.priority]}>{selected.priority}</Tag></Descriptions.Item>
              <Descriptions.Item label="状态"><Tag color={STATUS_COLORS[selected.status]}>{selected.status}</Tag></Descriptions.Item>
              <Descriptions.Item label="来源">{selected.source}</Descriptions.Item>
              <Descriptions.Item label="处理人">{selected.assignee || '-'}</Descriptions.Item>
              <Descriptions.Item label="创建人">{selected.created_by}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{formatTime(selected.created_at)}</Descriptions.Item>
              <Descriptions.Item label="问题描述" span={2}>{selected.description}</Descriptions.Item>
              {selected.result_note && (
                <Descriptions.Item label="处理结果" span={2}>{selected.result_note}</Descriptions.Item>
              )}
              {selected.alarm && (
                <Descriptions.Item label="关联告警" span={2}>
                  <Tag color="red">{selected.alarm.level}</Tag> {selected.alarm.title}
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}
      </Modal>

      <Modal
        title="完成工单"
        open={completeOpen}
        onCancel={() => setCompleteOpen(false)}
        onOk={handleComplete}
        okText="确认完成"
      >
        <div style={{ marginBottom: 8 }}>处理结果说明</div>
        <Input.TextArea
          rows={4}
          value={resultNote}
          onChange={(e) => setResultNote(e.target.value)}
          placeholder="请填写故障原因、处理措施及恢复情况"
        />
      </Modal>
    </div>
  );
}
